import { useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";

import { ProfileCard } from "../../../types/profile";
import { BureauDashboardData } from "../dashboard.types";
import { normalizeProfileCard } from "../dashboard.utils";

type BureauInfo = {
  mobileNumber?: string | null;
  bureauName?: string | null;
};

type UseDirectRegisteredProfilesParams = {
  dashboardData: BureauDashboardData | null;
  bureauInfo?: BureauInfo | null;
};

const useDirectRegisteredProfiles = ({
  dashboardData,
  bureauInfo,
}: UseDirectRegisteredProfilesParams) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const profiles = useMemo(
    () =>
      (dashboardData?.superAdminProfiles?.data ?? []).map(
        (profile: any, index: number) =>
          normalizeProfileCard(profile, index, "super-admin"),
      ),
    [dashboardData?.superAdminProfiles?.data],
  );

  const totalCount = useMemo(
    () =>
      Number(
        dashboardData?.superAdminProfiles?.total ?? profiles.length,
      ),
    [dashboardData?.superAdminProfiles?.total, profiles.length],
  );

  const openWhatsAppForProfile = useCallback(
    (profile: ProfileCard) => {
      const bureauMobileNumber = String(
        bureauInfo?.mobileNumber ||
          dashboardData?.bureauMobileNumber ||
          "",
      ).trim();

      const targetNumber = bureauMobileNumber.replace(/\D/g, "");

      if (!targetNumber) {
        toast.error("Your bureau mobile number is not available.");
        return;
      }

      const message = [
        "I need this profile",
        `Profile ID: ${profile.maritalId || "N/A"}`,
        `Name: ${profile.name || "N/A"}`,
        `Bureau Name: ${bureauInfo?.bureauName || "Self Registered"}`,
        `Mobile Number: ${bureauMobileNumber || "N/A"}`,
      ].join("\n");

      const whatsappUrl =
        `https://wa.me/${targetNumber}` +
        `?text=${encodeURIComponent(message)}`;

      window.open(
        whatsappUrl,
        "_blank",
        "noopener,noreferrer",
      );
    },
    [
      bureauInfo?.bureauName,
      bureauInfo?.mobileNumber,
      dashboardData?.bureauMobileNumber,
    ],
  );

  useEffect(() => {
    const element = scrollerRef.current;

    if (!element || profiles.length <= 1) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!document.hasFocus()) {
        return;
      }

      const maxScrollLeft =
        element.scrollWidth - element.clientWidth;

      if (maxScrollLeft <= 0) {
        return;
      }

      const nextScrollLeft = element.scrollLeft + 160;

      element.scrollTo({
        left:
          nextScrollLeft >= maxScrollLeft
            ? 0
            : nextScrollLeft,
        behavior: "smooth",
      });
    }, 2800);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [profiles.length]);

  return {
    profiles,
    totalCount,
    scrollerRef,
    openWhatsAppForProfile,
  };
};

export default useDirectRegisteredProfiles;